import React, { useState } from 'react';

export interface ChecklistItem {
  readonly itemId: string;
  readonly title: string;
  readonly checked: boolean;
}

export interface ChecklistProps {
  readonly checklistId: string;
  readonly title: string;
  readonly items: ChecklistItem[];
  readonly onToggleItem: (checklistId: string, itemId: string, checked: boolean) => void;
  readonly onDeleteChecklist: (checklistId: string) => void;
  readonly onAddItem?: (checklistId: string, title: string) => void;
}

/**
 * Checklist component — displays items with checkboxes, progress indicator,
 * strikethrough on full completion, inline item add form, and delete action.
 *
 * Requirements: 13.1-13.5
 */
export function Checklist({
  checklistId,
  title,
  items,
  onToggleItem,
  onDeleteChecklist,
  onAddItem,
}: ChecklistProps): React.ReactElement {
  const completed = items.filter((i) => i.checked).length;
  const total = items.length;
  const allComplete = total > 0 && completed === total;
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');

  const handleAddItem = () => {
    const trimmed = newItemTitle.trim();
    if (!trimmed || !onAddItem) return;
    onAddItem(checklistId, trimmed);
    setNewItemTitle('');
    setShowAddItem(false);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className={`font-medium text-sm ${allComplete ? 'line-through text-muted-foreground' : 'text-card-foreground'}`}>
          {title}
        </h4>
        <div className="flex items-center gap-2">
          <span
            className="text-xs text-muted-foreground"
            aria-label={`${completed} of ${total} items complete`}
          >
            {completed}/{total}
          </span>
          <button
            onClick={() => onDeleteChecklist(checklistId)}
            aria-label={`Delete checklist: ${title}`}
            className="text-xs text-muted-foreground hover:text-destructive px-1"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-primary rounded-full transition-all"
            style={{ width: `${(completed / total) * 100}%` }}
          />
        </div>
      )}

      <div className="space-y-1">
        {items.map((item) => (
          <label
            key={item.itemId}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <input
              type="checkbox"
              checked={item.checked}
              onChange={(e) => onToggleItem(checklistId, item.itemId, e.target.checked)}
              className="rounded border-border"
            />
            <span className={item.checked ? 'line-through text-muted-foreground' : 'text-card-foreground'}>
              {item.title}
            </span>
          </label>
        ))}
      </div>

      {/* Add item form */}
      {onAddItem && (
        <div className="mt-2">
          {showAddItem ? (
            <div className="flex gap-2">
              <label htmlFor={`add-item-${checklistId}`} className="sr-only">New item title</label>
              <input
                id={`add-item-${checklistId}`}
                type="text"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddItem();
                  if (e.key === 'Escape') { setShowAddItem(false); setNewItemTitle(''); }
                }}
                placeholder="Item title..."
                className="flex-1 border border-border rounded-md px-2 py-1 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
              <button
                onClick={handleAddItem}
                className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-md"
              >
                Add
              </button>
              <button
                onClick={() => { setShowAddItem(false); setNewItemTitle(''); }}
                className="text-xs text-muted-foreground px-2 py-1"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddItem(true)}
              className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
              aria-label={`Add item to ${title}`}
            >
              + Add item
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddChecklistForm — inline form for creating a new checklist
// ---------------------------------------------------------------------------

export interface AddChecklistFormProps {
  readonly onSubmit: (title: string) => void;
  readonly onCancel: () => void;
}

export function AddChecklistForm({ onSubmit, onCancel }: AddChecklistFormProps): React.ReactElement {
  const [title, setTitle] = useState('');

  const handleSubmit = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setTitle('');
  };

  return (
    <div className="border border-border rounded-md p-3 bg-muted/30">
      <label htmlFor="new-checklist-title" className="text-xs font-medium text-muted-foreground uppercase mb-1 block">
        Checklist title
      </label>
      <div className="flex gap-2">
        <input
          id="new-checklist-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') onCancel();
          }}
          placeholder="Enter checklist title..."
          className="flex-1 border border-border rounded-md px-3 py-1.5 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          autoFocus
        />
        <button
          onClick={handleSubmit}
          className="bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-sm"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="text-muted-foreground px-3 py-1.5 rounded-md text-sm hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
